import { SkeletonText, Text, VStack } from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { ProfileProgress } from "../../../components/card/ProfileProgress";
import { PageContainer } from "../../../components/container/PageContainer";
import { TabsResellerRoot } from "../../../components/dashboard_reseller/TabsResellerRoot";
import ShareLinkModal from "../../../components/ShareLinkModal";
import { myStoreNavsReseller } from "../../../constants/PageNavs";
import { ResellerProfileInterface } from "../../../constants/ResellerProfile";
import useCustomToast from "../../../lib/useCustomToast";
import { useTriggerRenderStore } from "../../../store/useTriggerRenderStore";
import { useUserHeader } from "../../../store/useUserHeader";
import { useBgBaseColor } from "../../../themes/colors";
import { axiosReseller } from "../../../utils/AxiosInstance";
import { ProfileComplete } from "../../../utils/etc";
import { SpinnerLoading } from "../../../components/spinner/SpinnerLoading";

const ContainerEditableAvatar = lazy(
  () => import("../../../components/input/ContainerEditableAvatar")
);

const ContainerEditableBanner = lazy(
  () => import("../../../components/input/ContainerEditableBanner")
);

export const MyStoreProfile = () => {
  const [data, setData] = useState<ResellerProfileInterface | undefined>(
    undefined
  );
  const [loaded, setLoaded] = useState<boolean>(false);

  const [statusProfile, setStatusProfile] = useState<
    ProfileComplete[] | undefined
  >();
  const [countProgress, setCountProgress] = useState<number>(0);
  const toast = useCustomToast();
  const bgBase = useBgBaseColor();
  const { fetchUserHeader, fetchUserNonActive } = useUserHeader();
  const { statusData, setStatusData } = useTriggerRenderStore();

  useEffect(() => {
    setLoaded(false);

    axiosReseller
      .get(`/v1/reseller/profile-store`)
      .then((res) => {
        setData(res.data.data);
      })
      .catch((err) => {
        toast({
          description: err?.response?.data?.message || "",
        });
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [statusData]);

  useEffect(() => {
    let mustFilledResellerData: ProfileComplete[] = [
      {
        id: 1,
        name: "Banner",
        isComplete: !!data?.banner,
      },
      {
        id: 2,
        name: "Logo",
        isComplete: !!data?.logo,
      },
      {
        id: 3,
        name: "Nama Toko",
        isComplete: !!data?.name,
      },
      {
        id: 4,
        name: "URL Store",
        isComplete: !!data?.prefix,
      },
      {
        id: 5,
        name: "Deskripsi Store",
        isComplete: !!data?.description,
      },
      {
        id: 6,
        name: "Bank",
        isComplete: !!data?.bank_name && !!data?.acc_name && !!data?.acc_number,
      },
    ];

    setStatusProfile(mustFilledResellerData);
  }, [
    data?.acc_name,
    data?.acc_number,
    data?.bank_name,
    data?.banner,
    data?.description,
    data?.logo,
    data?.name,
    data?.prefix,
  ]);

  useEffect(() => {
    let total = 0;
    const arrMustFilledProfile = [
      data?.logo,
      data?.banner,
      data?.prefix,
      data?.description,
      data?.acc_name,
      data?.acc_number,
      data?.bank_name,
    ];
    arrMustFilledProfile &&
      arrMustFilledProfile.map((item) => {
        !!item ? (total += 15) : (total += 0);
      });

    setCountProgress(total);
  }, [
    data?.acc_name,
    data?.acc_number,
    data?.bank_name,
    data?.banner,
    data?.description,
    data?.logo,
    data?.prefix,
  ]);

  const refreshAvatar = () => {
    setStatusData();
    if (data?.resellerId) {
      fetchUserHeader(data?.resellerId, `${data?.role}`, true);
      fetchUserNonActive(data?.resellerId, `${data?.role}`, true);
    }
  };

  return (
    <PageContainer navs={myStoreNavsReseller}>
      <VStack className="mystoreprofile" w={"100%"} gap={4} align={"stretch"}>
        {countProgress < 100 && (
          <ProfileProgress
            title="Kelengkapan Profil Reseller"
            countProgress={countProgress}
            statusProfile={statusProfile}
          />
        )}

        <VStack
          className="hero"
          position={"relative"}
          h={["220px", "260px", "300px"]}
        >
          <Suspense>
            <ContainerEditableBanner
              mt={countProgress > 100 ? 4 : 0}
              name={data?.name}
              src={
                data?.banner
                  ? `${process.env.REACT_APP_MEDIA_URL}${data.banner}`
                  : ""
              }
              pageType={2}
              endpoint={`/v1/reseller/update-store-banner`}
              onRefreshBanner={() => setStatusData()}
            />
          </Suspense>

          <Suspense fallback={<SpinnerLoading />}>
            <ContainerEditableAvatar
              name={data?.name || "BL"}
              alignSelf={"center"}
              pageType={2}
              borderWidth={"4px"}
              borderColor={bgBase}
              position={"absolute"}
              bottom={0}
              endpoint={`/v1/reseller/update-store-logo`}
              src={
                data?.logo
                  ? `${process.env.REACT_APP_MEDIA_URL}${data?.logo}`
                  : ""
              }
              onRefreshAvatar={() => refreshAvatar()}
            />
          </Suspense>
        </VStack>

        <VStack gap={0} textAlign={"center"}>
          <SkeletonText noOfLines={!loaded ? 1 : undefined} isLoaded={loaded}>
            <Text textStyle={"h-reg-title"}>{data?.name}</Text>
          </SkeletonText>

          {data?.prefix && loaded && (
            <Text textStyle={"b-reg"} variant={"secondary"}>
              {`${process.env.REACT_APP_FRONTEND_URL}/reseller/${data?.prefix}`}
            </Text>
          )}

          {data?.prefix && loaded && (
            <ShareLinkModal
              fontSize="10px"
              iconSize="16px"
              buttonText="Bagikan Link Toko"
              modalTitle="Bagikan Toko"
              modalDescription="Anda bisa membagikan toko ini melalui media sosial."
              mx={"auto"}
              data={`${process.env.REACT_APP_FRONTEND_URL}/reseller/${data?.prefix}`}
            />
          )}
        </VStack>

        <TabsResellerRoot
          data={data}
          mt={4}
          children={undefined}
          loaded={loaded}
          onRefresh={() => setStatusData()}
        />
      </VStack>
    </PageContainer>
  );
};
